import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpContext} from "@angular/common/http";
import {Observable} from "rxjs";
import {BaseService} from "./base.service";
import {CookieService} from "./cookie.service";
import {SKIP_LOADER} from "./loader.service";
import {OWN_CLIENT_ERROR_TEXT, PASS_REDIRECT_RESPONSE} from "./http-interceptor.service";
import {AssistantMessage, AssistantReply, AssistantResetReply, AssistantStatus} from "../interfaces/assistant";

/** Ключ історії чату в sessionStorage */
const HISTORY_KEY = 'assistant_chat';
/** Скільки останніх повідомлень тримаємо в історії вкладки */
const HISTORY_LIMIT = 60;
/** Ключ вибору вигляду вікна в localStorage (картка #980): 'panel' - вузька панель справа */
const LAYOUT_KEY = 'assistant_layout';
/** Ключ у localStorage (картка #982): вікно лишили відкритим - відкриваємо його й після перезавантаження */
const OPEN_KEY = 'assistant_open';

/** Історія в sessionStorage - разом з акаунтом, якому вона належить */
interface StoredHistory {
  account: string;
  messages: AssistantMessage[];
}

/**
 * ІІ-помічник кабінету (картка #963): запити web_api/assistant/* і історія розмови вкладки.
 *
 * Історія - у sessionStorage лише для показу у вікні; що пам'ятає модель, тримає бек (Redis, по id
 * користувача). Історія прив'язана до акаунта (кука email + «дивитись як»): інший вхід або інший
 * клієнт у «дивитись як» її не бачить - вона стирається при читанні, а також при виході (signOut),
 * відмові в доступі й закінченні сесії.
 */
@Injectable({
  providedIn: 'root'
})
export class AssistantService extends BaseService {
  private http = inject(HttpClient);
  private cookie = inject(CookieService);

  /** GET assistant/status - показувати помічника цьому користувачу. Фоновий: без лоадера. */
  status(): Observable<AssistantStatus> {
    return this.http.get<AssistantStatus>(this.apiRoot + 'assistant/status', {
      context: new HttpContext().set(SKIP_LOADER, true),
    });
  }

  /**
   * POST assistant/message. Відповідь іде до 50 с - без повноекранного лоадера; текст відмови 403/429
   * вікно показує саме (без загального тосту); коди кінця сесії віддаємо вікну, щоб воно стерло історію.
   * reset - бек забуває розмову перед питанням.
   */
  message(message: string, reset: boolean): Observable<AssistantReply> {
    return this.http.post<AssistantReply>(this.apiRoot + 'assistant/message', reset ? {message, reset} : {message}, {
      context: this.chatContext(),
    });
  }

  /** POST assistant/reset - бек забуває розмову */
  reset(): Observable<AssistantResetReply> {
    return this.http.post<AssistantResetReply>(this.apiRoot + 'assistant/reset', {}, {
      context: this.chatContext(),
    });
  }

  /** Історія поточного акаунта; чужу (інший вхід, інший «дивитись як») стираємо */
  loadHistory(): AssistantMessage[] {
    let stored: StoredHistory | null = null;

    try {
      stored = JSON.parse(sessionStorage.getItem(HISTORY_KEY) || 'null');
    } catch (e) {
      stored = null;
    }

    if (!stored || stored.account !== this.currentAccount() || !Array.isArray(stored.messages)) {
      this.forget();

      return [];
    }

    return stored.messages
      .filter((message) =>
        message && (message.role === 'user' || message.role === 'assistant') && typeof message.text === 'string'
      )
      .map((message) => {
        // Кнопки-уточнення (#966) - лише цілі: напис і текст рядками
        const options = Array.isArray(message.options)
          ? message.options.filter((option) => option && typeof option.text === 'string' && typeof option.send === 'string')
          : [];

        return {role: message.role, text: message.text, ...(message.error ? {error: true} : {}), ...(options.length ? {options} : {})};
      });
  }

  saveHistory(messages: AssistantMessage[]): void {
    const stored: StoredHistory = {
      account: this.currentAccount(),
      messages: messages.slice(-HISTORY_LIMIT).map((message) => ({
        role: message.role,
        text: message.text,
        ...(message.error ? {error: true} : {}),
        ...(message.options && message.options.length ? {options: message.options} : {}),
      })),
    };

    try {
      sessionStorage.setItem(HISTORY_KEY, JSON.stringify(stored));
    } catch (e) {
      // Сховище переповнене чи заборонене - історія просто не переживе перезавантаження
    }
  }

  /** Стерти історію вкладки (вихід, зміна користувача, відмова в доступі) */
  forget(): void {
    sessionStorage.removeItem(HISTORY_KEY);
  }

  /** Картка #980: клієнт обрав вузьку панель справа - так і відкриваємо, в усіх вкладках і після виходу */
  loadPanelMode(): boolean {
    try {
      return localStorage.getItem(LAYOUT_KEY) === 'panel';
    } catch (e) {
      return false;
    }
  }

  savePanelMode(panel: boolean): void {
    try {
      if (panel) {
        localStorage.setItem(LAYOUT_KEY, 'panel');
      } else {
        localStorage.removeItem(LAYOUT_KEY);
      }
    } catch (e) {
      // Сховище заборонене - вибір діє до перезавантаження
    }
  }

  /**
   * Картка #982: вікно не закрили - після перезавантаження відкриваємо знову. Лише «відкрито/закрито», без даних
   * клієнта; знімає закриття вікна, кінець сесії у вікні й вихід (header signOut)
   */
  loadOpenState(): boolean {
    try {
      return localStorage.getItem(OPEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  saveOpenState(open: boolean): void {
    try {
      if (open) {
        localStorage.setItem(OPEN_KEY, '1');
      } else {
        localStorage.removeItem(OPEN_KEY);
      }
    } catch (e) {
      // Сховище заборонене - вікно просто не відкриється саме після перезавантаження
    }
  }

  private chatContext(): HttpContext {
    return new HttpContext()
      .set(SKIP_LOADER, true)
      .set(OWN_CLIENT_ERROR_TEXT, true)
      .set(PASS_REDIRECT_RESPONSE, true);
  }

  // Як OrganizationsService.currentAccount: кабінет бек будує для акаунта входу (кука email)
  // або для підміненого через «дивитись як»
  private currentAccount(): string {
    return this.cookie.getCookie('email') + '|' + this.cookie.getCookie('admin_goods_look_like_user');
  }
}
